import { StaticBaseDao } from '@/dao';
import {
    Participant,
    IntArray,
    PostResponse,
    MergeParticipantsRequest,
    DuplicateResultLine,
    DuplicateSearchResult,
    ParticipantSearchRequest,
    ParticipantLicenceResponse,
    ParticipantId,
    SearchRequest,
    TypeAheadItem,
} from '@/objects/generated';
import type { DuplicateMergeSource } from './../enums/DuplicateMergeSource';
import { useUserStore } from '@/stores';

export class ParticipantDao extends StaticBaseDao {
    public static async Participants(uids: number[], detailed: boolean): Promise<Participant[]> {
        return this.BaseFetchArray(
            Participant,
            `Participant/Participants` +
                '?' +
                (uids == undefined ? '' : `${this.makeGetStringFromList(uids, 'uids')}`) +
                '&' +
                (detailed == undefined ? '' : `detailed=${detailed}`),
            'GET',
        );
    }
    public static async MarkAsNotDuplicates(uids: IntArray): Promise<PostResponse | null> {
        return this.BaseFetch(PostResponse, `Participant/MarkAsNotDuplicates`, 'POST', uids);
    }
    public static async Merge(mergeParticipantsRequest: MergeParticipantsRequest): Promise<PostResponse | null> {
        return this.BaseFetch(PostResponse, `Participant/Merge`, 'POST', mergeParticipantsRequest);
    }
    public static async GetDuplicates(
        ParticipantIDs: IntArray,
        Threshold: number,
        GroupByYear: boolean,
        source: DuplicateMergeSource,
    ): Promise<DuplicateResultLine[]> {
        return this.BaseFetchArray(
            DuplicateResultLine,
            `Participant/GetDuplicates` +
                '?' +
                (Threshold == undefined ? '' : `Threshold=${Threshold}`) +
                '&' +
                (GroupByYear == undefined ? '' : `GroupByYear=${GroupByYear}`) +
                '&' +
                (source == undefined ? '' : `source=${source}`),
            'POST',
            ParticipantIDs,
        );
    }
    public static async GetParticipantsForDuplicateMergeSource(source: DuplicateMergeSource): Promise<Participant[]> {
        return this.BaseFetchArray(
            Participant,
            `Participant/GetParticipantsForDuplicateMergeSource` + '?' + (source == undefined ? '' : `source=${source}`),
            'GET',
        );
    }
    public static async GetPreCalculatedDuplicateResults(): Promise<DuplicateSearchResult[]> {
        return this.BaseFetchArray(DuplicateSearchResult, `Participant/GetPreCalculatedDuplicateResults`, 'GET');
    }
    public static async GetPreCalculatedDuplicateResult(id: number): Promise<DuplicateResultLine[]> {
        return this.BaseFetchArray(
            DuplicateResultLine,
            `Participant/GetPreCalculatedDuplicateResult` + '?' + (id == undefined ? '' : `id=${id}`),
            'POST',
        );
    }
    public static async ParticipantSearch(searchRequest: ParticipantSearchRequest): Promise<Participant[]> {
        return this.BaseFetchArray(Participant, `Participant/ParticipantSearch`, 'POST', searchRequest);
    }
    public static async ParticipantLicenceSearch(searchRequest: ParticipantSearchRequest): Promise<ParticipantLicenceResponse | null> {
        return this.BaseFetch(ParticipantLicenceResponse, `Participant/ParticipantLicenceSearch`, 'POST', searchRequest);
    }
    public static async GetParticipants(uids: IntArray, eventId: number): Promise<Participant[]> {
        return this.BaseFetchArray(
            Participant,
            `Participant/GetParticipants` + '?' + (eventId == undefined ? '' : `eventId=${eventId}`),
            'POST',
            uids,
        );
    }
    public static async SaveParticipants(participants: Participant[]): Promise<IntArray | null> {
        const { user } = useUserStore();
        if (user === null || !user.IsSuperAdmin) {
            return null;
        }
        return this.BaseFetch(IntArray, `Participant/SaveParticipants`, 'POST', participants);
    }
    public static async GetParticipantIDForTypes(ids: IntArray, participantID: number): Promise<ParticipantId[]> {
        return this.BaseFetchArray(
            ParticipantId,
            `Participant/GetParticipantIDForTypes` + '?' + (participantID == undefined ? '' : `participantID=${participantID}`),
            'POST',
            ids,
        );
    }
    public static async ParticipantTypeaheadSearch(request: SearchRequest): Promise<TypeAheadItem[]> {
        return this.BaseFetchArray(TypeAheadItem, `Participant/ParticipantTypeaheadSearch`, 'POST', request);
    }
}
